import cx from 'classnames';
import Input from 'components/Globals/Base/Input';
import * as CS from 'components/Globals/Base/Input/InputStyles';
import type React from 'react';
import { type ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';
import nFormatter from 'utils/numberUtils';
import * as S from './RangeInputStyles';

export interface Props {
  label?: string;
  required?: boolean;
  hasDisclaimer?: boolean;
  id: string;
  value: { min: number; max: number };
  minValue: number;
  maxValue: number;
  step: number;
  size: 'small' | 'large';
  onChange: (maxValue: number, minValue: number) => void;
  className?: string;
  isCurrency?: boolean;
  variant?: 'brand' | 'default';
  dataCy?: string;
}
const RangeInput = ({
  id,
  label,
  value,
  required = false,
  hasDisclaimer = false,
  minValue,
  maxValue,
  size,
  isCurrency = false,
  onChange,
  step = 1,
  className = '',
  variant = 'default',
  dataCy = '',
}: Props) => {
  const [min, setMin] = useState(value.min);
  const [max, setMax] = useState(value.max);
  const [hasMinInputError, setHasMinInputError] = useState(false);
  const [hasMaxInputError, setHasMaxInputError] = useState(false);
  const range = useRef<HTMLDivElement>(null);
  const leftThumbRef = useRef<HTMLDivElement>(null);
  const rightThumbRef = useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies: MaxValue and Step don't change within this component as they are props passed down to it. No need to rerender when they change here
  useEffect(() => {
    setMin(value.min);
    if (maxValue - value.max < step) setMax(maxValue);
    else setMax(value.max);
  }, [value.min, value.max]);

  // Convert to percentage
  const getPercent = useCallback(
    (val) => Math.round(((val - minValue) / (maxValue - minValue)) * 100),
    [minValue, maxValue],
  );

  const minPercent = getPercent(value.min);
  const maxPercent = getPercent(value.max);

  const updateMinValue = () => {
    const isMinValid = min >= minValue && min <= value.max;

    if (isMinValid) {
      setMin(min);
      onChange(max, min);
    } else {
      setHasMinInputError(true);
      // revert to previously selected min value and remove the error state
      setTimeout(() => {
        setMin(value.min);
        onChange(max, value.min);
        setHasMinInputError(false);
      }, 200);
    }
  };

  const updateMaxValue = () => {
    const isMaxValid = max <= maxValue && max >= value.min;
    if (isMaxValid) {
      setMax(max);
      onChange(max, min);
    } else {
      setHasMaxInputError(true);
      // revert to previously selected min value and remove the error state
      setTimeout(() => {
        setMax(value.max);
        onChange(value.max, min);
        setHasMaxInputError(false);
      }, 200);
    }
  };

  const handleMinInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      updateMinValue();
      event.currentTarget.blur();
    }
  };

  const handleMaxInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      updateMaxValue();
      event.currentTarget.blur();
    }
  };

  const handleMinInputChange = (num: string) => {
    const val = parseInt(num.replace(/\D/g, ''), 10) || 0;
    setMin(val);
  };

  const handleMaxInputChange = (num: string) => {
    const val = parseInt(num.replace(/\D/g, ''), 10) || 0;
    setMax(val);
  };

  const handleMinRangeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const val = Math.min(+event.target.value, max - step);
    setMin(val);
    onChange(max, val);
  };

  const handleMaxRangeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const val = Math.max(+event.target.value, min + step);
    setMax(val);
    onChange(val, min);
  };

  return (
    <S.Wrapper className={className}>
      {label && (
        <CS.Label
          htmlFor={id}
          data-cy={`${dataCy}-label`}
        >
          {label} {required && <span>*</span>}
          {hasDisclaimer && <span className='disclaimer'>*</span>}
        </CS.Label>
      )}
      <S.InputsWrapper>
        <Input
          id={`min-${id}`}
          className={cx({ error: hasMinInputError })}
          value={
            isCurrency
              ? new Intl.NumberFormat('us-US', {
                  currency: 'USD',
                  currencyDisplay: 'narrowSymbol',
                  maximumFractionDigits: 0,
                  minimumFractionDigits: 0,
                  style: 'currency',
                }).format(min)
              : min
          }
          size={{ default: size }}
          type='text'
          pattern='[0-9.,]+'
          onChange={handleMinInputChange}
          onBlur={updateMinValue}
          onKeyDown={handleMinInputKeyDown}
          required={required}
          data-cy={`${dataCy}-min-input`}
        />
        <Input
          id={`max-${id}`}
          className={cx({ error: hasMaxInputError })}
          value={
            isCurrency
              ? new Intl.NumberFormat('us-US', {
                  currency: 'USD',
                  currencyDisplay: 'narrowSymbol',
                  maximumFractionDigits: 0,
                  minimumFractionDigits: 0,
                  style: 'currency',
                }).format(max)
              : max
          }
          size={{ default: size }}
          type='text'
          pattern='[0-9.,]+'
          onChange={handleMaxInputChange}
          onBlur={updateMaxValue}
          onKeyDown={handleMaxInputKeyDown}
          required={required}
          data-cy={`${dataCy}-max-input`}
        />
      </S.InputsWrapper>
      <S.RangeWrapper>
        <S.RangeInput
          type='range'
          id={`from-${id}-range`}
          name={id}
          min={minValue}
          max={maxValue}
          value={value.min}
          step={step}
          onChange={handleMinRangeChange}
          style={{ zIndex: min > max - 100 ? '5' : '3' }}
          tabIndex={-1}
          $variant={variant}
          aria-label={nFormatter(value.min)}
        />
        <S.RangeInput
          type='range'
          id={`to-${id}-range`}
          name={id}
          min={minValue}
          max={maxValue}
          value={value.max}
          step={step}
          onChange={handleMaxRangeChange}
          tabIndex={-1}
          $variant={variant}
          aria-label={nFormatter(value.max)}
        />
        <S.SliderWrapper>
          <S.SliderTrack />
          <S.SliderRange
            ref={range}
            $variant={variant}
            $minPercent={minPercent}
            $maxPercent={maxPercent}
          />
          <S.SliderThumb
            className='left'
            ref={leftThumbRef}
            $variant={variant}
            $minPercent={minPercent}
            $maxPercent={maxPercent}
            $width={Number(leftThumbRef?.current?.clientWidth)}
            data-cy={`${dataCy}-min-thumb`}
          >
            {nFormatter(value.min)}
          </S.SliderThumb>
          <S.SliderThumb
            className='right'
            ref={rightThumbRef}
            $variant={variant}
            $minPercent={minPercent}
            $maxPercent={maxPercent}
            $width={Number(rightThumbRef?.current?.clientWidth)}
            data-cy={`${dataCy}-max-thumb`}
          >
            {nFormatter(value.max)}
          </S.SliderThumb>
        </S.SliderWrapper>
      </S.RangeWrapper>
    </S.Wrapper>
  );
};

export default RangeInput;
